import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  applicableDeadlines,
  canAcceptProposalNow,
  CATEGORY_DEFAULT_MARGIN,
  FIXED_FEE_TRANSPORT,
  isAmendedTrip,
  lineClientTotal,
  type MarginType,
  type ProposalCategory,
  proposalChecklist,
  type ProposalDeadlineKey,
  type ProposalDto,
  type ProposalLineMath,
  PROPOSAL_CATEGORIES,
  PROPOSAL_DEADLINE_KEYS,
  type TripDto,
} from "@tc/shared";
import {
  Button,
  Card,
  CardHead,
  CardTitle,
  CostTotal,
  DeadlineSetupRow,
  DepositAuthorize,
  DocChip,
  Icon,
  Modal,
  OptionList,
  OptionRow,
  Select,
  Textarea,
  TextInput,
  Timeline,
} from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { useFormatters } from "@/lib/i18n/format";
import { ApiError } from "@/features/trips/api";
import { RequestSummaryCard } from "@/features/trips/RequestSummaryCard";
import { DocumentsPanel } from "@/features/records/DocumentsTab";
import { recordsApi, runRecordPdf } from "@/features/records/api";
import { proposalApi } from "./api";
import { UnselectedOptionsCard } from "./UnselectedOptionsCard";

/** Line-category → i18n key (the existing camelCase `builder.cat*` strings from the prototype port). */
const CATEGORY_LABEL: Record<ProposalCategory, string> = {
  cat_transport: "builder.catTransport",
  cat_hotel: "builder.catHotel",
  cat_transfers: "builder.catTransfers",
  cat_baggage: "builder.catBaggage",
  cat_extras: "builder.catExtras",
  cat_other: "builder.catOther",
};

/** Local, free-text editor state for one line (numbers kept as strings so inputs edit freely). */
interface EditorLine {
  key: string;
  category: ProposalCategory;
  description: string;
  qty: string;
  unitCost: string;
  marginType: MarginType;
  marginValue: string;
}

const uid = () => crypto.randomUUID();

function blankLine(): EditorLine {
  return {
    key: uid(),
    category: "cat_other",
    description: "",
    qty: "1",
    unitCost: "0",
    marginType: "pct",
    marginValue: String(CATEGORY_DEFAULT_MARGIN.cat_other),
  };
}

/** The economics half of an editor line, parsed into the shape the shared maths consumes. */
function toMath(l: EditorLine): ProposalLineMath {
  return {
    qty: Number(l.qty) || 0,
    unitCost: Number(l.unitCost) || 0,
    marginType: l.marginType,
    marginValue: Number(l.marginValue) || 0,
  };
}

/** Whole nights between the trip's travel dates (0 if either is missing or it's a day trip). */
export function nightsOf(trip: TripDto): number {
  if (!trip.travelStartDate || !trip.travelEndDate) return 0;
  const ms = new Date(trip.travelEndDate).getTime() - new Date(trip.travelStartDate).getTime();
  return Math.max(0, Math.round(ms / 86_400_000));
}


/** Map a persisted line back into editor state (category persisted since TW UAT round 1). */
function fromDto(line: ProposalDto["lineItems"][number]): EditorLine {
  return {
    key: line.id,
    category: line.category ?? "cat_other",
    description: line.description,
    qty: String(line.qty),
    unitCost: String(line.unitCost),
    marginType: line.marginType,
    marginValue: String(line.marginValue),
  };
}

/** Editor state for one adaptive deadline row: toggleable on/off plus an ISO date the operator edits. */
interface DeadlineRow {
  key: ProposalDeadlineKey;
  labelKey: string;
  on: boolean;
  date: string;
}

/** Today + N days as an ISO date (`YYYY-MM-DD`) — seeds a fresh deadline at its default offset. */
function addDaysIso(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

/**
 * Build the deadline rows that apply to this trip (TW-3444). The set is adaptive — acceptance +
 * deposit always, flight/train rows only for the requested transport (the shared
 * `applicableDeadlines`). A saved proposal seeds each row from its persisted column (on iff the date
 * is set); a fresh trip seeds every row enabled at its default offset, ready for TC to adjust.
 */
function seedDeadlines(trip: TripDto, proposal: ProposalDto | null): DeadlineRow[] {
  return applicableDeadlines(trip.transportModes).map((def) => {
    const saved = proposal ? proposal[def.key] : null;
    return {
      key: def.key,
      labelKey: def.labelKey,
      on: proposal ? saved != null : true,
      date: saved ?? addDaysIso(def.defaultOffsetDays),
    };
  });
}

/**
 * Fold the deadline rows into the save payload: an enabled row sends its date, a disabled (or
 * not-applicable, hence absent) row sends `null` so the column is cleared. Always covers all three
 * standard keys — the server replaces the whole deadline set on each save.
 */
function deadlinePayload(rows: DeadlineRow[]): Record<ProposalDeadlineKey, string | null> {
  return Object.fromEntries(
    PROPOSAL_DEADLINE_KEYS.map((key) => {
      const row = rows.find((r) => r.key === key);
      return [key, row && row.on ? row.date : null];
    }),
  ) as Record<ProposalDeadlineKey, string | null>;
}

/** Editor state for one custom deadline row TC adds (TW UAT round 2): a free-text label + an ISO date. */
interface CustomDeadlineRow {
  key: string;
  label: string;
  date: string;
}

/** Seed the custom-deadline editor rows from a saved proposal (empty for a fresh / unsaved one). */
function seedCustomDeadlines(proposal: ProposalDto | null): CustomDeadlineRow[] {
  return (proposal?.customDeadlines ?? []).map((cd) => ({ key: cd.id, label: cd.label, date: cd.date }));
}

/** Fold the custom-deadline rows into the save payload, dropping incomplete ones (no label or date). */
function customDeadlinePayload(rows: CustomDeadlineRow[]): { label: string; date: string }[] {
  return rows
    .filter((r) => r.label.trim().length > 0 && r.date)
    .map((r) => ({ label: r.label.trim(), date: r.date }));
}

/**
 * Proposal tab on a trip. The builder is TC's surface — back office prices the trip (accommodation is
 * just line items, TW UAT round 1) and sends it (TW-3443/3444/3445); a club user gets the review
 * experience: the itemised options it selects, transport, the deadline checklist, the TC message, and
 * the accept action with its deposit authorisation. The server enforces the split (build/send/
 * apply-update are back-office only; accept is club only; reading is shared and RLS-scoped).
 */
/**
 * Download the trip's proposal as a TC-branded PDF (TW-3451). Available to both audiences (the
 * proposal is club-visible); generation runs in the worker and is delivered via a signed URL.
 */
export function ProposalPdfButton({ tripId }: { tripId: string }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      icon="download"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await runRecordPdf(
            () => recordsApi.requestProposalPdf(tripId),
            (exportId) => recordsApi.proposalPdfStatus(tripId, exportId),
          );
        } catch {
          /* best-effort; the manifest/PDF worker may be offline in local dev (TW-3459) */
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? t("records.generatingPdf") : t("records.downloadPdf")}
    </Button>
  );
}

/**
 * `children`, when given, render inside the club review's left column beneath the cost breakdown —
 * used by the ProposalSent one-page view to drop the Documents and Messages panels into the same
 * layout. `onVersion` lets that page lift the proposal version into its own header (and signals the
 * review to drop its in-card version/PDF row, since the page renders them). Back office (the builder)
 * ignores both.
 */
export function ProposalTab({
  trip,
  children,
  onVersion,
  onSent,
  hideStatusCard,
}: {
  trip: TripDto;
  children?: ReactNode;
  onVersion?: (version: number) => void;
  /** Back-office only: a successful send completed (so the page can leave edit mode → STATE 2). */
  onSent?: () => void;
  /** Club only: drop the aside "Proposal accepted" card (the Accepted/Completed Overview shows its
   * own accepted header), leaving just the deadlines card. */
  hideStatusCard?: boolean;
}) {
  const { isBackOffice } = useAuthz();
  if (isBackOffice) return <ProposalBuilder trip={trip} onVersion={onVersion} onSent={onSent} />;
  return (
    <ClubProposalView trip={trip} onVersion={onVersion} hideStatusCard={hideStatusCard}>
      {children}
    </ClubProposalView>
  );
}

// ── Club review/accept view (TW-3445) ────────────────────────────────────────────

type ClubLoad =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; proposal: ProposalDto | null };

/** Whole days from today until an ISO date (negative once past) — drives the deadline tone/label. */
function daysUntil(iso: string): number {
  const due = new Date(iso);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/**
 * The club's read-only proposal experience (TW-3445). Renders only once TC has *sent* the proposal —
 * a Draft (TC still building, or a revision in progress) shows a "being prepared" placeholder, since
 * the club shouldn't see an unsent quote. A Sent proposal can be accepted; once Accepted, the deposit
 * can be acknowledged (a manual acknowledgement only — no payment is taken here).
 */
function ClubProposalView({
  trip,
  children,
  onVersion,
  hideStatusCard = false,
}: {
  trip: TripDto;
  children?: ReactNode;
  onVersion?: (version: number) => void;
  /** Drop the aside "Proposal accepted" card when the embedding page already shows an accepted
   * header (the Accepted/Completed Overview tab) — leaves the deadlines card as the only aside. */
  hideStatusCard?: boolean;
}) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  // When the embedding page renders the version/PDF in its own header (via onVersion), drop the
  // in-card header row so they aren't shown twice.
  const pageOwnsHeader = onVersion != null;
  const [state, setState] = useState<ClubLoad>({ kind: "loading" });
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState(false);
  // The club's line-item selection (every option pre-selected by default on load, TW UAT round 2 —
  // the club deselects what it doesn't want) + deposit authorisation, both submitted with accept.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [depositAuthorized, setDepositAuthorized] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    setSelectedIds(new Set());
    setDepositAuthorized(false);
    try {
      const proposal = await proposalApi.get(trip.id);
      setState({ kind: "ready", proposal });
      if (proposal) {
        onVersion?.(proposal.version);
        // Pre-select every option by default (TW UAT round 2); the club unticks what it doesn't want.
        // Harmless once accepted (selection is only used while the proposal is still selectable).
        setSelectedIds(new Set(proposal.lineItems.map((l) => l.id)));
      }
    } catch {
      setState({ kind: "error" });
    }
  }, [trip.id, onVersion]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runAction(fn: () => Promise<unknown>): Promise<void> {
    setActing(true);
    setActionError(false);
    try {
      await fn();
      await load();
    } catch (err) {
      setActionError(true);
      if (err instanceof ApiError) console.error(err.message);
    } finally {
      setActing(false);
    }
  }

  if (state.kind === "loading") return <div className="empty">{t("common.loading")}</div>;
  if (state.kind === "error")
    return (
      <Card>
        <div className="auth-alert" role="alert">
          <Icon name="alert" size={16} className="aa-ic" />
          <div className="aa-body">{t("builder.loadError")}</div>
        </div>
      </Card>
    );

  const { proposal } = state;

  // A null or still-Draft proposal isn't visible to the club yet — TC is preparing/revising it (also
  // the state right after a resubmit, while TC rebuilds the quote).
  if (!proposal || proposal.status === "Draft") {
    return (
      <Card>
        <div className="empty">
          <div className="empty-ic">
            <Icon name="doc" size={22} />
          </div>
          <div style={{ fontWeight: 600 }}>{t("proposal.preparingTitle")}</div>
          <div className="sub">{t("proposal.preparingDesc")}</div>
        </div>
      </Card>
    );
  }

  const accepted = proposal.acceptedAt != null;
  // An expired trip/proposal (the acceptance deadline passed) renders the same review — read-only,
  // with the accept aside swapped for a Resubmit CTA into the pre-populated request wizard, where
  // submitting reactivates the trip (Expired → Enquiry server-side; UAT round 3 follow-up).
  const expired = trip.status === "Expired" || proposal.status === "Expired";
  // The club amended the request after this proposal went out: the review on screen is outdated, so
  // accepting is blocked (card disabled + alert; the server enforces the same marker with a 409)
  // until TC reviews the changes and publishes an updated version.
  const amendedPending = isAmendedTrip(trip);
  // While Sent (not yet accepted) the club picks its line items; once accepted the surviving lines
  // are the agreed scope and the list is read-only (as is a lapsed, expired or amendment-blocked review).
  const selectable = proposal.status === "Sent" && !accepted && !expired && !amendedPending;
  const requiresDeposit = nightsOf(trip) > 0;
  const items = proposalChecklist(proposal, trip.transportModes, {
    manifestSubmitted: trip.manifestSubmittedAt != null,
    tripCompleted: trip.status === "Completed",
  });

  // Group the line items by category so the club's option list reads as "Accommodation / Transport / …".
  const groups = (() => {
    const m = new Map<ProposalCategory, ProposalDto["lineItems"]>();
    for (const line of proposal.lineItems) {
      const cat = (line.category ?? "cat_other") as ProposalCategory;
      const list = m.get(cat) ?? [];
      list.push(line);
      m.set(cat, list);
    }
    return [...m.entries()];
  })();

  const fullTotal = proposal.totalAmount ?? proposal.lineItems.reduce((sum, line) => sum + line.amount, 0);
  // Live total reflects the club's current selection while choosing; the agreed total once accepted.
  const total = selectable
    ? proposal.lineItems.filter((l) => selectedIds.has(l.id)).reduce((sum, l) => sum + l.amount, 0)
    : fullTotal;
  const pax = trip.paxCount ?? 0;
  const perPerson = pax > 0 ? Math.round(total / pax) : total;

  const toggleLine = (id: string) => {
    setActionError(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const canAccept = canAcceptProposalNow({ selectedCount: selectedIds.size, requiresDeposit, depositAuthorized });

  return (
    <>
      {!pageOwnsHeader && (
        <div className="row-between" style={{ alignItems: "center", marginBottom: "var(--gap)" }}>
          <span className="tag">
            {t("proposal.version")} {proposal.version}
          </span>
          <ProposalPdfButton tripId={trip.id} />
        </div>
      )}
      {isAmendedTrip(trip) && (
        // The club amended the request after this proposal went out — TC hasn't answered yet, so the
        // review on screen predates the amendment (UAT round 3 follow-up).
        <div className="info-banner" role="status" style={{ marginBottom: "var(--gap)" }}>
          <Icon name="clock" size={16} />
          <span>{t("proposal.amendedHint")}</span>
        </div>
      )}
      <div className="prop-grid">
        <div className="stack" style={{ gap: "var(--gap)" }}>
          <Card>
            <CardTitle>{t("proposal.breakdown")}</CardTitle>
            {selectable && (
              <p className="sub" style={{ margin: "6px 0 0" }}>{t("proposal.selectHint")}</p>
            )}
            <div style={{ marginTop: 14 }}>
              <OptionList>
                {groups.flatMap(([, glines]) => glines).map((line) => {
                  // In read-only (accepted) mode every surviving line is part of the agreed scope, so
                  // it always reads as "included"; while selecting, a deselected line is struck through.
                  const included = !selectable || selectedIds.has(line.id);
                  return (
                    <OptionRow
                      key={line.id}
                      selectable={selectable}
                      checked={included}
                      onToggle={() => toggleLine(line.id)}
                      description={line.description}
                      qtyLabel={`${line.qty} × ${fmt.currency(line.unitPrice, proposal.currency)}`}
                      amountLabel={fmt.currency(line.amount, proposal.currency)}
                    />
                  );
                })}
              </OptionList>
            </div>
            <CostTotal
              label={selectable ? t("proposal.selectedTotal") : t("proposal.total")}
              perPersonLabel={`${fmt.currency(perPerson, proposal.currency)} ${t("proposal.perPerson")} · ${pax} ${t("units.pax")}`}
              amountLabel={fmt.currency(total, proposal.currency)}
            />
          </Card>

          <UnselectedOptionsCard lines={proposal.unselectedLineItems} currency={proposal.currency} />

          {/* TC's covering message is delivered into the trip's conversation (Messages tab) on send,
              not shown as a separate card here (TW UAT round 2). */}
          {children}
        </div>

        <div className="summary-aside stack" style={{ gap: 14 }}>
          {expired ? (
            // The lapsed review keeps its full breakdown alongside, but can no longer be accepted —
            // the way forward is resubmitting the request (the wizard opens pre-populated; submitting
            // moves the trip Expired → Enquiry and notifies TC).
            <Card>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Icon name="clock" size={22} className="faint" />
                <b style={{ fontSize: 15 }}>{t("proposal.expiredTitle")}</b>
              </div>
              <p className="sub" style={{ fontSize: 12, margin: "10px 0 14px" }}>
                {t("proposal.expiredDesc")}
              </p>
              <Button
                variant="primary"
                full
                icon="send"
                onClick={() => navigate(`/trips/${trip.id}/edit`)}
              >
                {t("proposal.resubmit")}
              </Button>
            </Card>
          ) : accepted ? (
            hideStatusCard ? null : (
            <Card style={{ background: "var(--success-soft)", borderColor: "transparent" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--success)" }}>
                <Icon name="checkCircle" size={22} />
                <b style={{ fontSize: 15 }}>{t("proposal.accepted")}</b>
              </div>
              {proposal.depositAuthorizedAt && (
                <p className="sub" style={{ fontSize: 12, margin: "10px 0 0" }}>
                  {t("proposal.depositAuthorizedOn", { date: fmt.date(proposal.depositAuthorizedAt) })}
                </p>
              )}
            </Card>
            )
          ) : (
            <Card
              style={
                amendedPending
                  ? undefined // blocked: drop the call-to-action emphasis along with the controls
                  : { borderColor: "var(--primary)", boxShadow: "0 0 0 3px var(--primary-soft)" }
              }
            >
              <CardTitle>{t("proposal.accept")}</CardTitle>
              <p className="muted" style={{ fontSize: 13, margin: "6px 0 16px" }}>{t("proposal.reviewIntro")}</p>
              {amendedPending && (
                <div className="auth-alert" role="alert" style={{ marginBottom: 14 }}>
                  <Icon name="alert" size={16} className="aa-ic" />
                  <div className="aa-body">{t("proposal.amendedBlocked")}</div>
                </div>
              )}
              {requiresDeposit && (
                <DepositAuthorize
                  checked={depositAuthorized}
                  disabled={amendedPending}
                  onChange={(checked) => {
                    setActionError(false);
                    setDepositAuthorized(checked);
                  }}
                  label={t("proposal.depositAuth")}
                />
              )}
              <Button
                variant="primary"
                full
                icon="check"
                disabled={acting || !canAccept || amendedPending}
                onClick={() =>
                  runAction(() =>
                    proposalApi.accept(trip.id, { selectedLineItemIds: [...selectedIds], depositAuthorized }),
                  )
                }
              >
                {acting ? t("common.loading") : t("proposal.accept")}
              </Button>
              {!canAccept && !amendedPending && (
                <p className="sub" style={{ fontSize: 11.5, textAlign: "center", margin: "8px 0 0" }}>
                  {selectedIds.size < 1 ? t("proposal.needsSelection") : t("proposal.needsDepositAuth")}
                </p>
              )}
              {/* "Request changes" is retired: the club changes the request directly via Edit
                  request (an amendment TC answers), and proposal feedback goes through Messages. */}
              {actionError && (
                <p className="sub" role="alert" style={{ fontSize: 11.5, textAlign: "center", margin: "8px 0 0", color: "var(--danger)" }}>
                  {t("proposal.acceptError")}
                </p>
              )}
            </Card>
          )}

          <Card>
            <CardHead>
              <CardTitle>{t("proposal.deadlines")}</CardTitle>
              <Icon name="clock" size={18} className="faint" />
            </CardHead>
            {items.length === 0 ? (
              <div className="sub" style={{ marginTop: 10 }}>—</div>
            ) : (
              <Timeline
                variant="deadline"
                style={{ marginTop: 14 }}
                items={items.map((dl, i) => {
                  const overdue = !dl.done && daysUntil(dl.date) < 0;
                  return {
                    key: dl.key,
                    node: dl.done ? <Icon name="check" size={12} stroke={2.5} /> : i + 1,
                    title: dl.label ?? (dl.labelKey ? t(dl.labelKey) : ""),
                    sub: fmt.date(dl.date),
                    state: dl.done ? "done" : overdue ? "overdue" : "pending",
                    meta: dl.done ? (
                      <DocChip status="ok" label={t("proposal.dlDone")} />
                    ) : (
                      <span
                        className="dlc-when"
                        style={{
                          color: overdue ? "var(--danger)" : "var(--text-faint)",
                          background: overdue ? "var(--danger-soft)" : "var(--surface-3)",
                        }}
                      >
                        {overdue ? t("proposal.dlOverdue") : t("proposal.dlDue")}
                      </span>
                    ),
                  };
                })}
              />
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

type LoadState = { kind: "loading" } | { kind: "error" } | { kind: "ready" };

function ProposalBuilder({
  trip,
  onVersion,
  onSent,
}: {
  trip: TripDto;
  /** Lift the loaded proposal version into the page header (shown next to the status badge). */
  onVersion?: (version: number) => void;
  /** A successful send completed — the page leaves edit mode and reloads (→ STATE 2). */
  onSent?: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [lines, setLines] = useState<EditorLine[]>([]);
  const [message, setMessage] = useState("");
  const [deadlines, setDeadlines] = useState<DeadlineRow[]>([]);
  const [customDeadlines, setCustomDeadlines] = useState<CustomDeadlineRow[]>([]);
  const [version, setVersion] = useState<number | null>(null);
  const [status, setStatus] = useState<ProposalDto["status"] | null>(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const [savedNotice, setSavedNotice] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [sendNotice, setSendNotice] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [updateError, setUpdateError] = useState(false);
  const [revisedNotice, setRevisedNotice] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const proposal = await proposalApi.get(trip.id);
      if (proposal) {
        setLines(proposal.lineItems.length > 0 ? proposal.lineItems.map(fromDto) : [blankLine()]);
        setMessage(proposal.message ?? "");
      } else {
        // A fresh request starts with one empty line — the operator builds the options from scratch
        // (no auto-derived flight/hotel lines from the request).
        setLines([blankLine()]);
        setMessage("");
      }
      setDeadlines(seedDeadlines(trip, proposal));
      setCustomDeadlines(seedCustomDeadlines(proposal));
      setVersion(proposal?.version ?? null);
      setStatus(proposal?.status ?? null);
      setState({ kind: "ready" });
    } catch {
      setState({ kind: "error" });
    }
    // Keyed on trip.id only — `trip` is read just to seed deadline rows; re-running on an unrelated
    // re-render would clobber TC's in-progress edits.
  }, [trip.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Report the current version up to the page header (save/revise/send all bump it).
  useEffect(() => {
    if (version !== null) onVersion?.(version);
  }, [version, onVersion]);

  const dirtyReset = useCallback(() => {
    setSavedNotice(false);
    setSaveError(false);
    setSendNotice(false);
    setSendError(false);
    setUpdateError(false);
    setRevisedNotice(false);
  }, []);
  const setLine = (key: string, patch: Partial<EditorLine>) => {
    dirtyReset();
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };
  const addLine = () => {
    dirtyReset();
    setLines((ls) => [...ls, blankLine()]);
  };
  const delLine = (key: string) => {
    dirtyReset();
    setLines((ls) => ls.filter((l) => l.key !== key));
  };
  const toggleDeadline = (key: ProposalDeadlineKey) => {
    dirtyReset();
    setDeadlines((ds) => ds.map((d) => (d.key === key ? { ...d, on: !d.on } : d)));
  };
  const setDeadlineDate = (key: ProposalDeadlineKey, date: string) => {
    dirtyReset();
    setDeadlines((ds) => ds.map((d) => (d.key === key ? { ...d, date } : d)));
  };
  const addCustomDeadline = () => {
    dirtyReset();
    setCustomDeadlines((cs) => [...cs, { key: uid(), label: "", date: addDaysIso(14) }]);
  };
  const setCustomDeadline = (key: string, patch: Partial<CustomDeadlineRow>) => {
    dirtyReset();
    setCustomDeadlines((cs) => cs.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  };
  const delCustomDeadline = (key: string) => {
    dirtyReset();
    setCustomDeadlines((cs) => cs.filter((c) => c.key !== key));
  };

  // Changing the category refreshes the line's default margin (a % markup, or a fixed fee for
  // transport) — exactly the prototype's behaviour, only on an explicit category change.
  const changeCategory = (key: string, category: ProposalCategory) => {
    const marginType: MarginType = category === "cat_transport" ? "fixed" : "pct";
    const marginValue = category === "cat_transport" ? FIXED_FEE_TRANSPORT : CATEGORY_DEFAULT_MARGIN[category];
    setLine(key, { category, marginType, marginValue: String(marginValue) });
  };

  // The latest version is live (Sent/Accepted/Expired) — it is immutable server-side, so the state
  // on screen is an as-yet-unsaved revision: the first save creates the Draft vN+1 (UAT round 3).
  const revisingLive = status !== null && status !== "Draft";

  async function onSave() {
    setSaving(true);
    dirtyReset();
    try {
      // Drop blank-description lines (treated as removed); the server re-derives unit price / amount
      // / total via the same shared maths, so only the economics are sent.
      const payloadLines = lines
        .filter((l) => l.description.trim().length > 0)
        .map((l) => ({ category: l.category, description: l.description.trim(), ...toMath(l) }));
      const saved = await proposalApi.save(trip.id, {
        message: message.trim() ? message.trim() : undefined,
        lines: payloadLines,
        ...deadlinePayload(deadlines),
        customDeadlines: customDeadlinePayload(customDeadlines),
      });
      // Saving over a live version created the revision — call that out instead of a plain "saved".
      const bumped = version !== null && saved.version !== version;
      setLines(saved.lineItems.length > 0 ? saved.lineItems.map(fromDto) : [blankLine()]);
      setMessage(saved.message ?? "");
      setDeadlines(seedDeadlines(trip, saved));
      setCustomDeadlines(seedCustomDeadlines(saved));
      setVersion(saved.version);
      setStatus(saved.status);
      if (bumped) setRevisedNotice(true);
      else setSavedNotice(true);
    } catch (err) {
      setSaveError(true);
      if (err instanceof ApiError) console.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Send the persisted Draft to the club: save the current editor state first (so what's on screen is
  // what goes out), then POST send (Draft→Sent, trip Enquiry→ProposalSent, notify the club). Once
  // sent, editing is locked behind Revise (which clones a fresh Draft) — so a Sent proposal the club
  // is reviewing can't be silently mutated.
  async function onSend() {
    setSending(true);
    dirtyReset();
    try {
      const payloadLines = lines
        .filter((l) => l.description.trim().length > 0)
        .map((l) => ({ category: l.category, description: l.description.trim(), ...toMath(l) }));
      await proposalApi.save(trip.id, {
        message: message.trim() ? message.trim() : undefined,
        lines: payloadLines,
        ...deadlinePayload(deadlines),
        customDeadlines: customDeadlinePayload(customDeadlines),
      });
      const sent = await proposalApi.send(trip.id);
      setStatus(sent.status);
      setVersion(sent.version);
      setSendNotice(true);
      // Tell the page the proposal is sent so it leaves edit mode and reloads into STATE 2.
      onSent?.();
    } catch (err) {
      setSendError(true);
      if (err instanceof ApiError) console.error(err.message);
    } finally {
      setSending(false);
    }
  }

  // Publish the amendment directly onto the accepted trip (UAT round 3 follow-up): save the editor
  // state (creating/refreshing the Draft revision), then apply-update — the new version becomes the
  // live Accepted one in place, the club is notified, and no re-acceptance is needed. Offered next
  // to "Send for re-acceptance" so TC chooses per case; guarded by a confirm since it changes the
  // standing agreement immediately.
  async function onApplyUpdate() {
    setUpdating(true);
    dirtyReset();
    try {
      const payloadLines = lines
        .filter((l) => l.description.trim().length > 0)
        .map((l) => ({ category: l.category, description: l.description.trim(), ...toMath(l) }));
      await proposalApi.save(trip.id, {
        message: message.trim() ? message.trim() : undefined,
        lines: payloadLines,
        ...deadlinePayload(deadlines),
        customDeadlines: customDeadlinePayload(customDeadlines),
      });
      const updated = await proposalApi.applyUpdate(trip.id);
      setStatus(updated.status);
      setVersion(updated.version);
      setConfirmUpdate(false);
      // The trip stays Accepted; the page leaves edit mode and reloads into the agreed STATE 2.
      onSent?.();
    } catch (err) {
      setConfirmUpdate(false);
      setUpdateError(true);
      if (err instanceof ApiError) console.error(err.message);
    } finally {
      setUpdating(false);
    }
  }

  if (state.kind === "loading") return <div className="empty">{t("common.loading")}</div>;
  if (state.kind === "error")
    return (
      <Card>
        <div className="auth-alert" role="alert">
          <Icon name="alert" size={16} className="aa-ic" />
          <div className="aa-body">{t("builder.loadError")}</div>
        </div>
      </Card>
    );

  // A single short result note for the footer action bar (the summary rail that used to carry these
  // is gone). Save/send outcomes take priority; otherwise an unsaved revision explains itself.
  const footNote = updateError
    ? { msg: t("builder.updateError"), tone: "var(--danger)" }
    : sendError
      ? { msg: t("builder.sendError"), tone: "var(--danger)" }
      : saveError
        ? { msg: t("builder.saveError"), tone: "var(--danger)" }
        : sendNotice
          ? { msg: t("builder.sent"), tone: "var(--success)" }
          : revisedNotice
            ? { msg: t("builder.revisedNotice", { version }), tone: "var(--success)" }
            : savedNotice
              ? { msg: t("builder.draftSaved"), tone: "var(--success)" }
              : null;

  return (
    <div className="stack" style={{ gap: "var(--gap)" }}>
      {/* Revise (clone a fresh Draft to amend a Sent/Accepted proposal) and the version tag both live
          in the page header now, alongside the other lifecycle actions — not repeated here. */}
      <RequestSummaryCard trip={trip} />

      <Card>
        <CardTitle>{t("builder.tripOptions")}</CardTitle>
        <div className="line-editor" style={{ marginTop: 14 }}>
            <div className="line-head">
              <span>{t("builder.category")}</span>
              <span>{t("builder.lineLabel")}</span>
              <span>{t("builder.lineQty")}</span>
              <span>{t("builder.lineCost")}</span>
              <span>{t("builder.lineMargin")}</span>
              <span style={{ textAlign: "right" }}>{t("builder.lineTotal")}</span>
              <span />
            </div>
            {lines.map((l) => (
              <div className="line-row" key={l.key}>
                <Select
                  value={l.category}
                  onChange={(v) => changeCategory(l.key, v as ProposalCategory)}
                  options={PROPOSAL_CATEGORIES.map((c) => ({ value: c, label: t(CATEGORY_LABEL[c]) }))}
                />
                <TextInput
                  value={l.description}
                  placeholder={t("builder.lineLabel")}
                  onChange={(v) => setLine(l.key, { description: v })}
                />
                <TextInput
                  type="number"
                  inputClassName="mono"
                  value={l.qty}
                  onChange={(v) => setLine(l.key, { qty: v })}
                />
                <TextInput
                  type="number"
                  inputClassName="mono"
                  value={l.unitCost}
                  onChange={(v) => setLine(l.key, { unitCost: v })}
                />
                <span className="line-margin-in">
                  <TextInput
                    type="number"
                    inputClassName="mono"
                    value={l.marginValue}
                    onChange={(v) => setLine(l.key, { marginValue: v })}
                  />
                  <button
                    type="button"
                    className="margin-unit"
                    title={t("builder.marginToggleTitle")}
                    onClick={() => setLine(l.key, { marginType: l.marginType === "fixed" ? "pct" : "fixed" })}
                  >
                    {l.marginType === "fixed" ? "€" : "%"}
                  </button>
                </span>
                <span className="line-total-cell">{fmt.currency(lineClientTotal(toMath(l)))}</span>
                <button className="line-del" onClick={() => delLine(l.key)} aria-label={t("builder.removeLine")}>
                  <Icon name="x" size={15} />
                </button>
              </div>
            ))}
            <Button variant="soft" size="sm" icon="plus" className="add-line-btn" onClick={addLine}>
              {t("builder.addLine")}
            </Button>
          </div>
          <p className="sub" style={{ marginTop: 12 }}>
            {t("builder.perLineMarginHint")}
          </p>
        </Card>

      <DocumentsPanel trip={trip} />

      <Card>
        <CardTitle>{t("builder.deadlineSetup")}</CardTitle>
        <p className="sub" style={{ margin: "4px 0 12px" }}>{t("builder.deadlineHint")}</p>
        {deadlines.map((d) => (
          <DeadlineSetupRow
            key={d.key}
            label={t(d.labelKey)}
            enabled={d.on}
            onToggle={() => toggleDeadline(d.key)}
            date={d.date}
            onDateChange={(v) => setDeadlineDate(d.key, v)}
          />
        ))}

        {/* Custom deadlines TC can add beyond the standard set (TW UAT round 2). */}
        <div className="dl-custom">
          <div className="dl-custom-head">{t("builder.customDeadlines")}</div>
          {customDeadlines.length === 0 && (
            <div className="dl-custom-empty">{t("builder.customDeadlinesEmpty")}</div>
          )}
          {customDeadlines.map((c) => (
            <div className="dl-custom-row" key={c.key}>
              <TextInput
                className="dlc-name"
                value={c.label}
                placeholder={t("builder.deadlineLabelPlaceholder")}
                onChange={(v) => setCustomDeadline(c.key, { label: v })}
              />
              <TextInput
                type="date"
                inputClassName="mono"
                value={c.date}
                onChange={(v) => setCustomDeadline(c.key, { date: v })}
              />
              <button
                className="line-del"
                onClick={() => delCustomDeadline(c.key)}
                aria-label={t("builder.removeDeadline")}
              >
                <Icon name="x" size={15} />
              </button>
            </div>
          ))}
          <Button variant="soft" size="sm" icon="plus" className="add-line-btn" onClick={addCustomDeadline}>
            {t("builder.addDeadline")}
          </Button>
        </div>
      </Card>

      <Card>
        <CardTitle>{t("builder.proposalMessage")}</CardTitle>
        <p className="sub" style={{ margin: "4px 0 12px" }}>{t("builder.proposalMessageHint")}</p>
        <Textarea
          value={message}
          onChange={(v) => {
            dirtyReset();
            setMessage(v);
          }}
          rows={6}
          placeholder={t("builder.proposalMessagePlaceholder")}
        />
      </Card>

      <div className="proposal-foot">
        <div className="pf-note">
          {footNote ? (
            <span role="status" style={{ color: footNote.tone }}>
              {footNote.msg}
            </span>
          ) : revisingLive ? (
            // Editing over a live version: nothing has changed server-side yet — the first save
            // creates the revision Draft, and the club keeps seeing the live version until re-sent.
            <span className="sub">{t("builder.revisionHint", { next: (version ?? 0) + 1 })}</span>
          ) : null}
        </div>
        <div className="pf-actions">
          <Button variant="ghost" disabled={saving || sending || updating} onClick={onSave}>
            {saving ? t("common.loading") : t("builder.saveDraft")}
          </Button>
          {trip.status === "Accepted" ? (
            // The trip is already agreed, so TC picks how the amendment lands (UAT round 3
            // follow-up): apply it directly (stays Accepted, club notified — the club asked for the
            // change) or send it for re-acceptance (back to ProposalSent, club reviews afresh).
            <>
              <Button
                variant="secondary"
                icon="send"
                disabled={sending || saving || updating}
                onClick={onSend}
              >
                {sending ? t("common.loading") : t("builder.sendReacceptance")}
              </Button>
              <Button
                variant="primary"
                size="lg"
                icon="check"
                disabled={sending || saving || updating}
                onClick={() => setConfirmUpdate(true)}
              >
                {updating ? t("common.loading") : t("builder.updateProposal")}
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="lg"
              icon="send"
              disabled={sending || saving || updating}
              onClick={onSend}
            >
              {sending ? t("common.loading") : t("builder.sendProposal")}
            </Button>
          )}
        </div>
      </div>

      {confirmUpdate && (
        <Modal open onClose={() => setConfirmUpdate(false)} title={t("builder.updateConfirmTitle")}>
          <div className="modal-body">
            <p className="muted" style={{ margin: 0 }}>
              {t("builder.updateConfirmDesc")}
            </p>
          </div>
          <div className="modal-foot">
            <Button variant="ghost" onClick={() => setConfirmUpdate(false)} disabled={updating}>
              {t("actions.cancel")}
            </Button>
            <Button variant="primary" icon="check" onClick={onApplyUpdate} disabled={updating}>
              {updating ? t("common.loading") : t("builder.updateProposal")}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
